import React, {memo} from 'react';

import {Link} from 'react-router';
import styled from 'styled-components';

import {LazyTooltip} from 'app/components/general/LazyTooltip';
import ThreeDotsDropdownMenu from 'app/general/widgets/ThreeDotsDropdownMenu';
import {StyledDocItem} from 'app/procedures/styles';
import {getTeamDetailPageURL} from 'app/urls';
import {gt, ngt} from 'app/utils/i18n';

import type {actionsGetterType} from 'app/general/widgets/ThreeDotsDropdownMenu';
import type {TeamSerializedType} from 'sweet-types';

export const StyledTeamListItem = styled(StyledDocItem).attrs<{
  className?: string;
}>({
  className: 'row border-bottom py-2',
})`
  .badge-light.empty {
    opacity: 0.25;
  }
`;
type Props = {
  object: TeamSerializedType;
  isManager?: boolean;
  hideMembershipStatus?: boolean;
  actionsGetter?: actionsGetterType<TeamSerializedType>;
};
export const TeamItem = memo<Props>(
  ({object, isManager, hideMembershipStatus, actionsGetter}: Props) => (
    <StyledTeamListItem>
      <div className="col-12 col-lg text-truncate text-nowrap d-flex align-items-center">
        <Link
          to={getTeamDetailPageURL(object.id, object.slug)}
          className="text-dark font-weight-bold d-block d-sm-inline"
        >
          {/* show loading indicator while the user-team membership data is being fetched */}
          {hideMembershipStatus || isManager === false ? null : (
            <i
              className={`fa-fw fa mr-1 ${
                isManager ? 'fa-star' : 'fa-circle-notch fa-spin'
              }`}
            />
          )}
          {object.name || <i>{gt('Untitled Team')}</i>}
        </Link>
        <span className="pl-2 text-muted font-weight-light font-italic">
          {object.description}
        </span>
      </div>

      <div className="col d-flex align-items-center pt-2 pt-lg-0">
        <div className="mr-4">
          {object.number_of_procedures != null ? (
            <LazyTooltip
              tooltip={ngt(
                '%s procedure',
                '%s procedures',
                object.number_of_procedures
              )}
            >
              <span
                className={`ml-1 badge badge-light ${
                  object.number_of_procedures === 0 ? 'empty' : ''
                }`}
              >
                <i className="fa fa-file" /> {object.number_of_procedures}
              </span>
            </LazyTooltip>
          ) : null}

          <LazyTooltip
            tooltip={ngt(
              '%s process',
              '%s processes',
              object.number_of_processes
            )}
          >
            <span
              className={`ml-1 badge badge-light ${
                object.number_of_processes === 0 ? 'empty' : ''
              }`}
            >
              <i className="far fa-copy" /> {object.number_of_processes}
            </span>
          </LazyTooltip>

          <LazyTooltip
            tooltip={ngt(
              ' %s policy',
              '%s policies',
              object.number_of_policies
            )}
          >
            <span
              className={`ml-1 badge badge-light ${
                object.number_of_policies === 0 ? 'empty' : ''
              }`}
            >
              <i className="fa fa-umbrella" /> {object.number_of_policies}
            </span>
          </LazyTooltip>
        </div>

        <span>
          {[
            object.num_managers !== 0
              ? ngt('%d Manager', '%d Managers', object.num_managers)
              : null,
            object.num_teammates + object.num_readers !== 0
              ? ngt(
                  '%d Member',
                  '%d Members',
                  object.num_teammates + object.num_readers
                )
              : null,
          ]
            .filter((x) => x)
            .join(' / ')}
        </span>
      </div>
      <div className="col-1 col-sm-auto pl-0 d-flex align-items-start align-items-sm-center flex-row flex-column-reverse flex-sm-row">
        {actionsGetter != null ? (
          <ThreeDotsDropdownMenu
            forceMenu={true}
            actionsGetter={actionsGetter}
            object={object}
          />
        ) : (
          <span className="px-2 py-1">
            <i className="fa fa-fw" />
          </span>
        )}
      </div>
    </StyledTeamListItem>
  )
);
